import React from 'react';
import styled from 'styled-components';
import { Body5, Container } from '@ui';
import { IconButton } from '@ui/buttons';
import { Working } from '@ui/Working';
import ChatBubble from '@assets/icons/svgs/ChatBubble';
import { QuerySeriesContext } from '@/contexts/QuerySeriesContext';
import { RowChatContext } from './chat/RowChatContext';
import { parseReasoningResult } from './utils/parseReasoningResult';

const Wrapper = styled.div`
  overflow-x: auto;
  max-width: 100%;
`;

const TableWrapper = styled.table`
  width: 100%;
  table-layout: fixed;
  border-collapse: separate;
  border-spacing: 0;

  th,
  td {
    vertical-align: top;
    padding: 12px;
    border: 1px solid ${({ theme }) => theme.themeColors.lowContrastBorder};
  }

  th {
    background: ${({ theme }) => theme.themeColors.componentNoOpacity};
    text-align: left;
  }

  td:last-child {
    vertical-align: middle;
  }
`;

const AnswerWrapper = styled.div`
  white-space: pre-wrap;
`;

const ReasoningWrapper = styled.div`
  white-space: pre-wrap;
`;

const resolveRowValues = (row, hookRowResult) => {
  const parsedHook = parseReasoningResult(hookRowResult?.rawMessage || hookRowResult?.answer || '');
  return {
    answer: row.answer || parsedHook?.answer || '',
    reasoning: row.reasoning || parsedHook?.reasoning || '',
  };
};

const ChatIconWrapper = styled.div`
  display: flex;
  justify-content: center;
  color: ${({ theme }) => theme.themeColors.secondaryText};
  opacity: ${({ $disabled }) => ($disabled ? 0.3 : 1)};
  pointer-events: ${({ $disabled }) => ($disabled ? 'none' : 'auto')};

  &:hover {
    color: ${({ $disabled, theme }) => ($disabled ? undefined : theme.themeColors.text)};
  }
`;

export const QuerySeriesResultsTable = () => {
  const { rows, groupResultId, getRowResult, isRowComplete } = React.useContext(QuerySeriesContext);
  const { openChat } = React.useContext(RowChatContext);

  if (!rows || rows.length === 0) {
    return null;
  }

  return (
    <Container top={24}>
      <Wrapper>
        <TableWrapper>
          <colgroup>
            <col style={{ width: '20%' }} />
            <col style={{ width: '40%' }} />
            <col style={{ width: '40%' }} />
            <col style={{ width: '34px' }} />
          </colgroup>
          <thead>
            <tr>
              <th>
                <Body5 bold>Question</Body5>
              </th>
              <th>
                <Body5 bold>Answer</Body5>
              </th>
              <th>
                <Body5 bold>Reasoning</Body5>
              </th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const rowResult = getRowResult(groupResultId, row.rowIndex);
              const values = resolveRowValues(row, rowResult);
              const completed = isRowComplete(groupResultId, row.rowIndex) || row.status === 'completed';
              return (
                <tr key={row.rowIndex}>
                  <td>
                    <Body5>{row.displayQuestion || row.question}</Body5>
                  </td>
                  <td>
                    <AnswerWrapper>
                      {values.answer ? (
                        <Body5>{values.answer}</Body5>
                      ) : completed ? (
                        <Body5 isLowContrast>No answer found</Body5>
                      ) : (
                        <Working noVerticalPadding />
                      )}
                    </AnswerWrapper>
                  </td>
                  <td>
                    <ReasoningWrapper>
                      {values.reasoning ? (
                        <Body5>{values.reasoning}</Body5>
                      ) : completed ? (
                        <Body5 isLowContrast>No reasoning found</Body5>
                      ) : (
                        <Working noVerticalPadding />
                      )}
                    </ReasoningWrapper>
                  </td>
                  <td>
                    <ChatIconWrapper $disabled={!completed}>
                      <IconButton onClick={() => openChat(row.rowIndex)}>
                        <ChatBubble size={18} />
                      </IconButton>
                    </ChatIconWrapper>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </TableWrapper>
      </Wrapper>
    </Container>
  );
};
